import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { defaultTo, get, kebabCase } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Tabs from '@atlaskit/tabs';
import { TabData } from '@atlaskit/tabs/dist/types/types';
import {
  DynamicResultTable,
  FormattedCurrencyV2,
  FormattedDate,
  InvoiceUtil,
  NoDataContentInTable,
  commonTabs,
} from '@css-xp/base';
import {
  FetchInvoicesForTransactionAccountQueryVariables,
  FetchInvoicesForTransactionAccountV2Query,
  FetchInvoicesForTransactionAccountV2QueryVariables,
  Invoice,
  InvoiceFilterOption,
  InvoicePaymentMethod,
  InvoiceStatus,
  SortDirection,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT, NEW_DATE_TIME_FORMAT, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, emptyStateProp } from '@atlassian/cssxp-utils-entitlement';
import { creditDetailsInvoice } from '../../queries/credit-details-invoice-query';
import './filtered-invoice-table.scss';

interface Props {
  transactionAccountId: string;
  transactionNumber: string;
  label: string;
  pushPaymentMethodId?: string;
}

export enum InvoiceFilterType {
  ALL = 'All',
  PAID = 'Paid',
  PAYMENT_DEFERRED = 'Payment deferred',
  UNCOLLECTIBLE = 'Uncollectible',
}

export const defaultSearchOptions: FetchInvoicesForTransactionAccountQueryVariables['searchOptions'] = {
  first: 8,
  sort: {
    sortFields: ['createdDate', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};

const getFilters = (filterStatus: InvoiceFilterType): Array<{ key: string; value: string | undefined }> => {
  const tabFilter = [];
  if (getTabName(filterStatus) === InvoiceStatus.PAYMENT_DEFERRED) {
    tabFilter.push({ key: 'status', value: 'OPEN' });
  } else if (getTabName(filterStatus) === InvoiceStatus.PAID) {
    tabFilter.push({ key: 'status', value: 'PAYMENT_DEFERRED' });
  } else if (getTabName(filterStatus) === InvoiceStatus.UNCOLLECTIBLE) {
    tabFilter.push({ key: 'status', value: 'UNCOLLECTIBLE' });
  }
  return tabFilter;
};

const getTabName = (filterStatus: String) => {
  //@ts-ignore
  const item = Object.keys(InvoiceFilterType).find((item) => InvoiceFilterType[item] === filterStatus);
  return item;
};

const FilteredInvoiceTable: React.FC<Props> = ({
  transactionAccountId,
  transactionNumber,
  label,
  pushPaymentMethodId,
}) => {
  const location = useLocation();
  const history = useHistory();

  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [filterStatus, setfilterStatus] = useState<InvoiceFilterType>(InvoiceFilterType.ALL);
  const { data, loading, error, refetch } = useQuery<
    FetchInvoicesForTransactionAccountV2Query,
    FetchInvoicesForTransactionAccountV2QueryVariables
  >(creditDetailsInvoice, {
    variables: {
      id: transactionAccountId,

      searchOptions: {
        ...defaultSearchOptions,
        first: rowsPerPage,
        offset: 0,
      },
      filterOptions:
        getTabName(filterStatus) !== 'ALL'
          ? {
              and: [{ key: 'paymentMethodId', value: `${pushPaymentMethodId}` }, getFilters(filterStatus)[0]],
            }
          : { and: [{ key: 'paymentMethodId', value: `${pushPaymentMethodId}` }] },
    },
    notifyOnNetworkStatusChange: true,
    displayName: 'fetchInvoicesList',
    skip: !transactionAccountId,
  });

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          ...defaultSearchOptions,
          first: rowsPerPage,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        },
      });
    },
    [rowsPerPage, currentPage, refetch]
  );

  const handleRowChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);
  const invoices = React.useMemo(() => defaultTo(get(data, 'transactionAccount.invoices.nodes'), []), [data]);
  const totalCount = React.useMemo(() => defaultTo(get(data, 'transactionAccount.invoices.totalCount'), 0), [data]);

  const tabs = React.useMemo(() => {
    return [
      {
        label: InvoiceFilterType.ALL,
        content: '',
        testId: 'all-status-tab',
      },
      {
        label: InvoiceFilterType.PAID,
        content: '',
        testId: 'paid-status-tab',
      },
      {
        label: InvoiceFilterType.PAYMENT_DEFERRED,
        content: '',
        testId: 'payment-deferred-status-tab',
      },
      {
        label: InvoiceFilterType.UNCOLLECTIBLE,
        content: '',
        testId: 'uncollectible-status-tab',
      },
    ];
  }, []);

  const refetchRecords = (currentStatus: string) => {
    const filterStatus = getTabName(currentStatus) as InvoiceFilterType;
    refetch({
      searchOptions: {
        ...defaultSearchOptions,
        first: rowsPerPage,
        offset: 0,
      },
      filterOptions:
        getTabName(filterStatus) !== 'ALL'
          ? {
              and: [{ key: 'paymentMethodId', value: `${pushPaymentMethodId}` }, getFilters(filterStatus)[0]],
            }
          : { and: [{ key: 'paymentMethodId', value: `${pushPaymentMethodId}` }] },
    });
  };

  const updateUrlLocation = (tabName: string) => {
    history.push({
      hash: location?.hash,
      search: new URLSearchParams({ inner_tab: kebabCase(tabName) }).toString(),
    });
  };

  const handleTabSelect = React.useCallback(
    (selected: TabData) => {
      switch (selected.label) {
        case InvoiceFilterType.ALL: {
          setfilterStatus(InvoiceFilterType.ALL);
          refetchRecords(InvoiceFilterType.ALL);
          break;
        }
        case InvoiceFilterType.PAID: {
          setfilterStatus(InvoiceFilterType.PAID);
          refetchRecords(InvoiceFilterType.PAID);
          break;
        }
        case InvoiceFilterType.PAYMENT_DEFERRED: {
          setfilterStatus(InvoiceFilterType.PAYMENT_DEFERRED);
          refetchRecords(InvoiceFilterType.PAYMENT_DEFERRED);
          break;
        }
        case InvoiceFilterType.UNCOLLECTIBLE: {
          setfilterStatus(InvoiceFilterType.UNCOLLECTIBLE);
          refetchRecords(InvoiceFilterType.UNCOLLECTIBLE);
          break;
        }
      }

      // Updating the location object.
      {
        selected.label && updateUrlLocation(getTabName(selected.label) as string);
      }
    },
    [data, filterStatus]
  );

  const headData = React.useMemo(() => {
    const cells = [
      {
        content: 'Invoice number',
        width: 15,
      },
      {
        content: 'Billing account',
        width: 15,
      },
      {
        content: 'Due',
        width: 15,
      },
      {
        content: 'Duning date',
        width: 15,
      },
      {
        content: 'Issued on',
        width: 15,
      },
      {
        content: 'Amount',
        width: 10,
      },
      {
        content: 'Outstanding balance',
        width: 15,
      },
    ];

    return {
      cells,
    };
  }, []);

  const invoiceRows = React.useMemo(() => {
    const rows = defaultTo(invoices, []).map((invoice: Invoice) => {
      const { grandTotal, prePaymentCreditNotesAmount } = invoice;
      const totalOutstanding =
        grandTotal && prePaymentCreditNotesAmount ? grandTotal - prePaymentCreditNotesAmount : grandTotal;
      const dunningDate = invoice.dunningStatus?.pop()?.nextPaymentAttempDate || null;
      return {
        key: invoice?.id,
        cells: [
          {
            content: (
              <>
                <commonTabs.InvoiceCell
                  invoiceId={invoice.id}
                  status={invoice?.status}
                  invoiceNumber={invoice.invoiceNumber || ''}
                  paymentError={!!invoice?.dunningStatus?.length}
                  pdfLink={invoice.pdfLink}
                  transactionAccountId={transactionAccountId}
                  grandTotal={invoice.grandTotal}
                  sourceSystem={invoice.sourceSystem || ''}
                  paymentMethod={invoice.paymentMethod as InvoicePaymentMethod}
                />
              </>
            ),
          },
          {
            content: (
              <>
                <a href={`${TRANSACTION_ACCOUNT_PATH_2}/${transactionNumber}`} target="_blank">
                  {transactionNumber}
                </a>
              </>
            ),
          },
          {
            content: (
              <>
                {invoice.dueDate ? (
                  <div
                    className={classNames({
                      'overdue-date': InvoiceUtil.isDueDateOverdue(
                        status ?? '',
                        !!invoice?.dunningStatus?.length,
                        invoice.dueDate
                      ),
                    })}
                  >
                    <FormattedDate date={invoice.dueDate} format={NEW_DATE_FORMAT} />
                  </div>
                ) : (
                  <NoDataContentInTable />
                )}
              </>
            ),
          },
          {
            content: <>{dunningDate ? <FormattedDate date={dunningDate} /> : <NoDataContentInTable />}</>,
          },
          {
            content: (
              <>
                {invoice.createdDate ? (
                  <FormattedDate date={invoice.createdDate} showTime={true} format={NEW_DATE_TIME_FORMAT} />
                ) : (
                  <NoDataContentInTable />
                )}
              </>
            ),
          },
          {
            content: (
              <>
                <span className="invoice-amount-cell">
                  {invoice.grandTotal ? (
                    <FormattedCurrencyV2
                      value={invoice.grandTotal}
                      currency={invoice.paymentCurrency}
                      showCurrencyCode
                    />
                  ) : (
                    <NoDataContentInTable />
                  )}
                </span>
              </>
            ),
          },
          {
            content: (
              <>
                {invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.PAYMENT_DEFERRED ? (
                  <FormattedCurrencyV2 value={0.0} currency={invoice.paymentCurrency} showCurrencyCode />
                ) : (
                  <FormattedCurrencyV2
                    value={totalOutstanding || 0}
                    currency={invoice.paymentCurrency}
                    showCurrencyCode
                  />
                )}
              </>
            ),
          },
        ],
      };
    });
    return rows;
  }, [data]);

  return (
    <div className="filtered-invoice-table" data-testid="all-invoice-tab">
      <DynamicResultTable
        testId="invoice-tab"
        error={error?.message}
        headingText={label}
        head={headData}
        rows={invoiceRows}
        isLoading={loading}
        emptyStateProps={emptyStateProp}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowChange={handleRowChange}
        isPaginationEnabled={invoices && totalCount > DEFAULT_ROWS_PER_PAGE}
        showFilters={true}
      >
        <div className="tabs">
          <Tabs tabs={tabs} onSelect={handleTabSelect} />
        </div>
      </DynamicResultTable>
    </div>
  );
};

export default FilteredInvoiceTable;
